import { useEffect, useState } from 'react';
import { api, type GameTip } from '@/api/client';
import { ErrorBox, Loading, PageTitle } from '@/components/ui';

export function TipsPage() {
  const [items, setItems] = useState<GameTip[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [creating, setCreating] = useState(false);

  const load = () =>
    api.tips()
      .then(({ items }) => setItems(items))
      .catch((e) => setErr(String(e)));

  useEffect(() => { void load(); }, []);

  if (err) return <ErrorBox text={err} />;
  if (!items) return <Loading />;

  async function add() {
    const text = draft.trim();
    if (!text) return;
    setCreating(true);
    try {
      await api.createTip({ text, sortOrder: items!.length });
      setDraft('');
      await load();
    } catch (e: any) { alert(e?.message); }
    setCreating(false);
  }

  return (
    <div>
      <PageTitle title="Match Tips" />
      <div className="card" style={{ padding: '10px 14px', marginBottom: 16, fontSize: 12, color: 'var(--ink-muted)', background: 'rgba(34,211,238,0.08)', borderColor: 'rgba(34,211,238,0.25)' }}>
        Gameplay hints that rotate on the <b>match screen</b> during a duel. Only <b>active</b> tips
        are shown; <b>sort order</b> sets the rotation sequence (lowest first). Keep each tip short
        (≤ 280 chars) so it fits the single-line strip on mobile.
      </div>

      <div className="card" style={{ padding: 14, marginBottom: 16, display: 'flex', gap: 8 }}>
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') void add(); }}
          maxLength={280}
          placeholder="New tip text…"
          style={{ flex: 1 }}
        />
        <button className="btn btn-primary" onClick={add} disabled={creating || !draft.trim()}>+ ADD TIP</button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {items.map((tip) => <TipRow key={tip.id} item={tip} onChanged={load} />)}
        {items.length === 0 && <div style={{ color: 'var(--ink-dim)', fontSize: 12 }}>No tips yet — add one above.</div>}
      </div>
    </div>
  );
}

function TipRow({ item, onChanged }: { item: GameTip; onChanged: () => void }) {
  const [text, setText] = useState(item.text);
  const [isActive, setIsActive] = useState(item.isActive);
  const [sortOrder, setSortOrder] = useState(item.sortOrder);
  const [busy, setBusy] = useState(false);

  const dirty = text !== item.text || isActive !== item.isActive || sortOrder !== item.sortOrder;

  async function save() {
    setBusy(true);
    try {
      await api.patchTip(item.id, { text: text.trim(), isActive, sortOrder });
      await onChanged();
    } catch (e: any) { alert(e?.message); }
    setBusy(false);
  }
  async function del() {
    if (!confirm('Delete this tip?')) return;
    setBusy(true);
    try { await api.deleteTip(item.id); await onChanged(); }
    catch (e: any) { alert(e?.message); }
    setBusy(false);
  }

  return (
    <div className="card" style={{ padding: 12, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', opacity: isActive ? 1 : 0.6 }}>
      <input
        type="number"
        value={sortOrder}
        onChange={(e) => setSortOrder(+e.target.value)}
        title="sort order"
        style={{ width: 60 }}
      />
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        maxLength={280}
        rows={2}
        style={{ flex: '1 1 280px', resize: 'vertical', minHeight: 38 }}
      />
      <label style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
        <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} /> active
      </label>
      <div style={{ display: 'flex', gap: 6 }}>
        <button className="btn btn-primary" onClick={save} disabled={busy || !dirty || !text.trim()}>SAVE</button>
        <button className="btn" onClick={del} disabled={busy} style={{ color: 'var(--c-red)' }}>DELETE</button>
      </div>
    </div>
  );
}
